import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Spinner } from "@/shared/ui/spinner";

import { useSplits } from "../api/use-splits";
import { SplitTable } from "../components/SplitTable";

function KpiCard({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div
      className="flex flex-col gap-1 rounded-[14px] border border-[var(--premium-border)] bg-[var(--surface-raised)] p-4 shadow-[var(--sh-card)]"
      data-testid="split-kpi-card"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
        {label}
      </span>
      <span className="text-2xl font-extrabold tabular-nums text-[var(--text-primary)]">
        {value}
      </span>
    </div>
  );
}

export function SplitOrdersPage(): JSX.Element {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();

  const { data: splits = [], isLoading } = useSplits();

  const totalLabels = splits.reduce((sum, s) => sum + s.count, 0);

  function handleOpen(sale: string): void {
    void navigate({ to: "/split-orders/$saleOrderId", params: { saleOrderId: sale } });
  }

  return (
    <div data-testid="split-orders-page">
      <PageHeader title={t("title_split")} />

      {isLoading ? (
        <div className="flex items-center justify-center py-16" data-testid="splits-loading">
          <Spinner size="lg" label={t("loading_orders")} />
        </div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label={t("kpi_splits")} value={splits.length} />
            <KpiCard label={t("kpi_labels")} value={totalLabels} />
          </div>

          <SplitTable splits={splits} onOpen={handleOpen} />
        </>
      )}
    </div>
  );
}
